import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Send } from 'lucide-react';
import { toast } from 'sonner';
import { instructorAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Field, btnGhost, btnPrimary, inputCls, panelCls } from '../../components/ui';

const STATUS_HINT = {
  pending: 'Your application is being reviewed.',
  approved: 'Approved! Sign in again to open your instructor dashboard.',
  rejected: 'Your last application was not approved — you may apply again.',
};

export default function ApplyPage() {
  const [state, setState] = useState(null); // {application, allow_instructor, is_author}
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [message, setMessage] = useState('');
  const [docPath, setDocPath] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = () => instructorAPI.myApplication().then(({ data }) => setState(data)).catch(() => setState({}));
  useEffect(() => {
    load();
  }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const { data } = await instructorAPI.applyUpload(file);
      setDocPath(data.url);
      toast.success(t('File uploaded'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await instructorAPI.apply({ phone, address, message, document_path: docPath });
      toast.success(t('Application submitted'));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Save failed'));
    } finally {
      setBusy(false);
    }
  };

  if (!state) return <p className="text-sm text-lms-muted">{t('Loading…')}</p>;

  if (state.is_author) {
    return (
      <div className="mx-auto max-w-md py-12 text-center">
        <p className="text-sm text-lms-muted">{t('You already have author access.')}</p>
        <Link to="/admin/courses" className={`${btnPrimary} mt-4`}>{t('Open the back-office')}</Link>
      </div>
    );
  }

  const app = state.application;
  const canApply = state.allow_instructor && (!app || app.status_label === 'rejected');

  return (
    <div className="mx-auto max-w-md">
      <h1 className="mb-2 text-xl font-semibold">{t('Become an instructor')}</h1>
      <p className="mb-6 text-sm text-lms-muted">{t('Teach on this platform and earn a share of every sale.')}</p>

      {app && (
        <div className={`${panelCls} mb-5 p-4 text-sm`}>
          <span className="text-lms-muted">{t('Application status')}: </span>
          <span className="font-medium">{t(app.status_label)}</span>
          <p className="mt-1 text-xs text-lms-faint">{t(STATUS_HINT[app.status_label] || '')}</p>
        </div>
      )}

      {!state.allow_instructor ? (
        <p className="text-sm text-lms-faint">{t('Instructor applications are closed right now.')}</p>
      ) : canApply ? (
        <form onSubmit={submit} className={`${panelCls} space-y-4 p-5`}>
          <Field label={t('Phone')}>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
          </Field>
          <Field label={t('Address')}>
            <input value={address} onChange={(e) => setAddress(e.target.value)} className={inputCls} />
          </Field>
          <Field label={t('Tell us about what you would teach')}>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} required className={inputCls} />
          </Field>
          <Field label={t('Supporting document (CV, portfolio — optional)')}>
            <div className="flex items-center gap-3">
              {docPath ? (
                <span className="truncate text-xs text-lms-muted">{docPath.split('/').pop()}</span>
              ) : (
                <span className="text-xs text-lms-faint">{t('No file yet')}</span>
              )}
              <label className={`${btnGhost} shrink-0 cursor-pointer`}>
                <FileText size={14} />
                {uploading ? t('Uploading…') : t('Upload')}
                <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.zip" className="hidden" disabled={uploading}
                  onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
              </label>
            </div>
          </Field>
          <button type="submit" disabled={busy || uploading} className={`${btnPrimary} w-full justify-center`}>
            <Send size={14} />
            {busy ? t('Submitting…') : t('Submit application')}
          </button>
        </form>
      ) : null}
    </div>
  );
}
